'use client';

import type { LocalCartInfo } from '@/types';
import * as Sentry from '@sentry/nextjs';
import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { RESET, atomWithStorage } from 'jotai/utils';
import { useCallback, useRef } from 'react';

const initialCartInfo: LocalCartInfo = {
  cartVersion: Number(process.env.NEXT_PUBLIC_LOCAL_CART_VERSION || 1),
  items: [],
};

const CART_STORAGE_KEY = 'cartInfo';

// カート情報のAtom(localStorage)
const _cartInfoAtom = atomWithStorage<LocalCartInfo>(CART_STORAGE_KEY, initialCartInfo);

const useCartInfoAtom = () => {
  const [cartInfo, setCartInfo] = useAtom(_cartInfoAtom);

  const clearCartInfo = useCallback(() => {
    setCartInfo(RESET);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [setCartInfo]);

  return [cartInfo, setCartInfo, clearCartInfo] as const;
};

// ログイン時のカート件数Atom（ヘッダーバッジ表示用）
// SSRで取得した初期値をセットし、クライアント側のカート操作で更新する
const _loginCartCountAtom = atom<number>(0);

const useLoginCartCountAtom = () => {
  return useAtom(_loginCartCountAtom);
};

// グローバルローディング用のAtom
const _globalLoadingAtom = atom(false);

const useGlobalLoadingAtom = () => {
  const [isGlobalLoading, setIsGlobalLoading] = useAtom(_globalLoadingAtom);

  // タイマーIDを保持するためのref
  //NOTE:処理が連続で走ると過去のタイマーがクリアされず爆速でロードが終わってしまう可能性があるのでIDを保持
  //NOTE:各処理で既存のタイマーをクリアする。ユーザーが短時間に複数回操作した場合、古いタイマーが残っていると予期せぬタイミングでローディングが停止してしまう
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startGlobalLoading = useCallback(() => {
    if (isGlobalLoading) return;
    setIsGlobalLoading(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    // 7秒後にローディングを停止
    timerRef.current = setTimeout(() => {
      setIsGlobalLoading(false);
      timerRef.current = null;
    }, 7000);
  }, [isGlobalLoading, setIsGlobalLoading]);

  const stopGlobalLoading = useCallback(() => {
    if (!isGlobalLoading) return;
    setIsGlobalLoading(false);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [isGlobalLoading, setIsGlobalLoading]);
  // WARNING: submit時など、画面遷移を伴わない場合はこちらを使用してください
  const forceStopGlobalLoading = useCallback(() => {
    setIsGlobalLoading(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [setIsGlobalLoading]);

  return [isGlobalLoading, startGlobalLoading, stopGlobalLoading, forceStopGlobalLoading] as const;
};

export { useCartInfoAtom, useLoginCartCountAtom, useGlobalLoadingAtom };
